import { PlacementXAnchor, PlacementYAnchor } from "building-types/annotation/annotationTypesEnum";
import type { LineStyle, TextStyle } from "building-types/views/Primitive2DOutput";
import { type SheetAssetGeometry } from "building-types/views/sheetGeometryTypes";
import { BUILT_IN_VIEW_SCALE_1_PER_4_INCH } from "building-types/views/viewScaleConstants";
import type { SheetPoints, SheetInches, OneHundredthMillimeters } from "common/types/nominalNumbersTypes";
import { assert } from "common/utils/assertOfType";
import { sheetInchesToPoints, oneHundredthMillimetersToInches } from "common/utils/nominalNumbers";
import type { Mutable } from "common/utils/types";
import { DIST_EPSILON } from "geom/core";
import type { Mat3 } from "geom/mat3";
import { mat3Dot, mat3Rotate, mat3Translate, mat3Scale, mat3Reduce, mat3Mult } from "geom/mat3";
import { polyLine2Transform } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { rect2ByBottomLeftWidthHeight } from "geom/rect2";
import { DXF_DEFAULT_STYLESHEET } from "../dxfConstants";
import type { DXFStyleSheet } from "../dxfParserTypes";
import { styleSheetMerge } from "../planStyles";
import { mat3TransformWithComponents } from "../sheetGeometryTools";
import { tagSelector } from "./primitiveStyles";
import type { SheetGeometryStyleSheet } from "./primitiveStylesTypes";

interface AssetDimensions {
  centerX: number;
  centerY: number;
  width: number;
  height: number;
  rotationRads?: number;
  outline: PolyLine2;
}

/**
 * @param assetGeometry
 * @returns Width and height of the content before any transforms are applied.
 */
function sheetAssetWidthHeight(assetGeometry: SheetAssetGeometry): { width: number; height: number } {
  if (assetGeometry.assetType === "DXF") {
    const aabb = assetGeometry.asset.geometryAABB2;
    // We rely on the aabb2 starting at 0,0. The DXF parser auto-aligns the content.
    assert(Math.abs(aabb.minX) < DIST_EPSILON && Math.abs(aabb.minY) < DIST_EPSILON);
    return { width: aabb.maxX, height: aabb.maxY };
  } else if (assetGeometry.assetType === "IMAGE") {
    return { width: assetGeometry.asset.width, height: assetGeometry.asset.height };
  } else {
    return { width: assetGeometry.width, height: assetGeometry.height };
  }
}

export function sheetAssetTransform(assetGeometry: SheetAssetGeometry): Mat3 {
  const {
    x = 0,
    y = 0,
    rotation = 0,
    placementXAnchor = PlacementXAnchor.CENTER,
    placementYAnchor = PlacementYAnchor.CENTER,
  } = assetGeometry;
  const scale = assetGeometry.assetType !== "IMAGE_LEGACY" ? assetGeometry.scale ?? 1 : 1;

  const mTranslate = mat3Translate(x, y);
  const mRotate = mat3Rotate(rotation);
  const mScale = mat3Scale(scale, scale);

  const getXAlignment = (anchor: PlacementXAnchor | PlacementYAnchor) => {
    if (anchor === PlacementXAnchor.LEFT) {
      return 0;
    } else if (anchor === PlacementXAnchor.CENTER) {
      return -0.5;
    } else {
      return -1;
    }
  };

  // DXF
  function getYAlignment(anchor: PlacementXAnchor | PlacementYAnchor) {
    if (anchor === PlacementYAnchor.BOTTOM) {
      return 0;
    } else if (anchor === PlacementYAnchor.CENTER) {
      return -0.5;
    } else {
      return -1;
    }
  }

  const { width, height } = sheetAssetWidthHeight(assetGeometry);
  const mAlign = mat3Translate(getXAlignment(placementXAnchor) * width, getYAlignment(placementYAnchor) * height);

  const transform = mat3Reduce([mTranslate, mRotate, mScale, mAlign]);

  if (assetGeometry.transform) {
    return mat3Mult(assetGeometry.transform, transform);
  }
  return transform;
}

export function sheetAssetDimensions(geometry: SheetAssetGeometry): AssetDimensions {
  const { width, height } = sheetAssetWidthHeight(geometry);

  const transform = sheetAssetTransform(geometry);

  const outline = rect2ByBottomLeftWidthHeight(0, 0, width, height);
  let centerX = width / 2;
  let centerY = height / 2;

  polyLine2Transform(outline, transform);
  [centerX, centerY] = mat3Dot(transform, centerX, centerY);

  const { transformScale, transformRotation } = mat3TransformWithComponents(transform);
  assert(Math.abs(Math.abs(transformScale[0]) - Math.abs(transformScale[1])) < 1e-4); // We should not be doing non-uniform scaling for assets.
  return {
    centerX,
    centerY,
    width: width * Math.abs(transformScale[0]),
    height: height * Math.abs(transformScale[0]),
    rotationRads: transformRotation,
    outline: outline,
  };
}

export function dxfStyleSheetToSheetGeometryStyleSheet(dxfStyleSheet: DXFStyleSheet): SheetGeometryStyleSheet {
  const geometrySylesheet: Mutable<SheetGeometryStyleSheet> = {};
  for (const tag in dxfStyleSheet) {
    const style = dxfStyleSheet[tag];
    const styleSelector = tagSelector(tag);
    const sheetGeometryStyle: Mutable<LineStyle & TextStyle> = {};
    if (style.lineWeight) {
      sheetGeometryStyle.linewidth = lineWeightToSheetPoints(style.lineWeight);
    }
    if (style.pattern && style.patternLength) {
      sheetGeometryStyle.dash = true;
      sheetGeometryStyle.dashLength = patternToSheetPoints(style.pattern);
    }
    geometrySylesheet[styleSelector] = sheetGeometryStyle;
  }

  // supply styles that are inherent to AutoCAD or other DXF viewers.
  // this sets the default lineweight to 0.25 mm and default text height to One unit (Inch for now)
  return styleSheetMerge(DXF_DEFAULT_STYLESHEET, geometrySylesheet);
}

function lineWeightToSheetPoints(lw: OneHundredthMillimeters): SheetPoints {
  return sheetInchesToPoints(oneHundredthMillimetersToInches(lw));
}

function patternToSheetPoints(pattern: number[]): SheetPoints[] {
  return pattern.map(
    (dl) => sheetInchesToPoints(dl as SheetInches) / BUILT_IN_VIEW_SCALE_1_PER_4_INCH,
  ) as SheetPoints[];
}
